import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Tab, Nav, Card, Button, Badge, Spinner } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';

const IITJAMEconomicsPage = () => {
  const [syllabusData, setSyllabusData] = useState([]);
  const [papersData, setPapersData] = useState([]);
  const [booksData, setBooksData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Simulate fetching data from database
  useEffect(() => {
    // In a real application, these would be API calls
    const fetchData = () => {
      setTimeout(() => {
        setSyllabusData([
          { 
            id: 1, 
            title: 'Microeconomics', 
            topics: ['Consumer Theory', 'Production & Costs', 'Market Structures', 'General Equilibrium', 'Welfare Economics', 'Information Economics', 'Game Theory'] 
          },
          { 
            id: 2, 
            title: 'Macroeconomics', 
            topics: ['National Income Accounting', 'Money & Banking', 'Inflation', 'Business Cycles', 'Economic Growth', 'Open Economy Macroeconomics', 'Classical & Keynesian Models'] 
          },
          { 
            id: 3, 
            title: 'Statistics & Econometrics', 
            topics: ['Probability Theory', 'Sampling Methods', 'Hypothesis Testing', 'Regression Analysis', 'Time Series Analysis', 'Index Numbers', 'Statistical Inference'] 
          },
          { 
            id: 4, 
            title: 'Mathematical Economics', 
            topics: ['Differential Calculus', 'Integral Calculus', 'Linear Algebra', 'Optimization Techniques', 'Difference & Differential Equations', 'Input-Output Models'] 
          },
          { 
            id: 5, 
            title: 'Indian Economy', 
            topics: ['Economic Development since Independence', 'Five-Year Plans', 'Economic Reforms', 'Banking System', 'Fiscal & Monetary Policy', 'Current Economic Issues'] 
          },
        ]);
        
        setPapersData([
          { id: 1, year: '2024', paperLink: '#', answerKeyLink: '#', difficulty: 'High' },
          { id: 2, year: '2023', paperLink: '#', answerKeyLink: '#', difficulty: 'Medium' },
          { id: 3, year: '2022', paperLink: '#', answerKeyLink: '#', difficulty: 'Medium' },
          { id: 4, year: '2021', paperLink: '#', answerKeyLink: '#', difficulty: 'High' },
          { id: 5, year: '2020', paperLink: '#', answerKeyLink: '#', difficulty: 'Medium' },
          { id: 6, year: '2019', paperLink: '#', answerKeyLink: '#', difficulty: 'Low' },
          { id: 7, year: '2018', paperLink: '#', answerKeyLink: '#', difficulty: 'Medium' },
          { id: 8, year: '2017', paperLink: '#', answerKeyLink: '#', difficulty: 'High' },
        ]);
        
        setBooksData([
          { 
            id: 1, 
            title: 'Microeconomic Theory', 
            author: 'Andreu Mas-Colell, Michael Whinston & Jerry Green', 
            coverImg: '/api/placeholder/200/300', 
            rating: 4.7,
            category: 'Microeconomics'
          },
          { 
            id: 2, 
            title: 'Macroeconomics', 
            author: 'Olivier Blanchard', 
            coverImg: '/api/placeholder/200/300', 
            rating: 4.5,
            category: 'Macroeconomics'
          },
          { 
            id: 3, 
            title: 'Introduction to Econometrics', 
            author: 'James H. Stock & Mark W. Watson', 
            coverImg: '/api/placeholder/200/300', 
            rating: 4.8,
            category: 'Econometrics'
          },
          { 
            id: 4, 
            title: 'Fundamental Methods of Mathematical Economics', 
            author: 'Alpha C. Chiang & Kevin Wainwright', 
            coverImg: '/api/placeholder/200/300', 
            rating: 4.6,
            category: 'Mathematical Economics'
          },
          { 
            id: 5, 
            title: 'Indian Economy', 
            author: 'Ramesh Singh', 
            coverImg: '/api/placeholder/200/300', 
            rating: 4.4,
            category: 'Indian Economy'
          },
        ]);
        
        setLoading(false);
      }, 1000);
    };
    
    fetchData();
  }, []);

  const internships = [
    {
      id: 1,
      title: "Economic Data Analysis with AI",
      description: "Apply machine learning algorithms to analyze economic data, forecast trends, and generate insights for policy recommendations.",
      duration: "3 months",
      stipend: "₹15,000/month",
      requirements: ["Basic programming skills (Python/R)", "Understanding of statistical methods", "Knowledge of economic indicators"],
      icon: "📊"
    },
    {
      id: 2,
      title: "Quantitative Research & Modeling",
      description: "Work with our research team to develop economic models using AI tools for market analysis, risk assessment, and portfolio optimization.",
      duration: "6 months",
      stipend: "₹20,000/month",
      requirements: ["Econometrics background", "Experience with statistical software", "Understanding of financial markets"],
      icon: "📈"
    },
    {
      id: 3,
      title: "Policy Analysis & Development",
      description: "Use data-driven approaches and AI tools to evaluate economic policies, simulate outcomes, and develop evidence-based recommendations.",
      duration: "4 months",
      stipend: "₹18,000/month",
      requirements: ["Strong analytical skills", "Knowledge of public economics", "Interest in policy research"],
      icon: "🏛️"
    }
  ];

  return (
    <div className="iit-jam-economics-page">
      {/* Hero Section */}
      <section className="hero-section text-white py-5 my-5">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <div className="hero-content">
                <Badge bg="light" text="dark" className="mb-2">Premier Institute</Badge>
                <h1 className="display-4 fw-bold mb-3">Excel in IIT-JAM Economics</h1>
                <p className="lead mb-4">Comprehensive coaching with data-driven internships in economic analysis and policy research. Join our program with a proven track record of 300+ successful candidates.</p>
                <div className="d-flex gap-3 flex-wrap">
                  <Button variant="light" size="lg" className="fw-bold">Enroll Now</Button>
                  <Button variant="outline-light" size="lg">Program Details</Button>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div className="hero-image text-center position-relative">
                <div className="hero-shape-1"></div>
                <div className="hero-shape-2"></div>
                <img src="/api/placeholder/500/350" alt="Economics classroom with students" className="img-fluid rounded-lg shadow position-relative z-index-1" />
                <div className="stats-card">
                  <div className="stats-item">
                    <h3>90%</h3>
                    <p>Success Rate</p>
                  </div>
                  <div className="stats-item">
                    <h3>10+</h3>
                    <p>Years Experience</p>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Coaching Information Section with Tabs */}
      <section className="coaching-section py-5">
        <Container>
          <div className="section-heading text-center mb-5">
            <Badge bg="primary" className="mb-2">Comprehensive Program</Badge>
            <h2 className="display-6 fw-bold">IIT-JAM Economics Preparation</h2>
            <p className="text-muted mx-auto" style={{ maxWidth: '700px' }}>Our systematic approach covers the entire IIT-JAM Economics syllabus with focus on conceptual clarity, problem-solving, and analytical skills</p>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status" className="mb-3">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <p className="text-muted">Loading course materials...</p>
            </div>
          ) : (
            <Tab.Container id="coaching-tabs" defaultActiveKey="syllabus">
              <Nav variant="pills" className="nav-tabs-custom justify-content-center mb-4">
                <Nav.Item>
                  <Nav.Link eventKey="syllabus">
                    <i className="bi bi-journal-text me-2"></i>Syllabus
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="papers">
                    <i className="bi bi-file-earmark-text me-2"></i>Previous Papers
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="books">
                    <i className="bi bi-book me-2"></i>Reference Books
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="program">
                    <i className="bi bi-calendar-check me-2"></i>Our Program
                  </Nav.Link>
                </Nav.Item>
              </Nav>
              
              <div className="tab-content-wrapper p-4 bg-white rounded-lg shadow-sm">
                <Tab.Content>
                  {/* Syllabus Tab */}
                  <Tab.Pane eventKey="syllabus">
                    <div className="syllabus-container">
                      <Row>
                        {syllabusData.map(unit => (
                          <Col md={6} lg={4} key={unit.id} className="mb-4">
                            <Card className="h-100 border-0 shadow-sm syllabus-card">
                              <Card.Header className="bg-gradient-primary text-white">
                                <h3 className="h5 mb-0">{unit.title}</h3>
                              </Card.Header>
                              <Card.Body className="p-4">
                                <ul className="topic-list">
                                  {unit.topics.map((topic, index) => (
                                    <li key={index} className="mb-2">
                                      <div className="d-flex align-items-center">
                                        <span className="topic-icon me-2">•</span>
                                        <span>{topic}</span>
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              </Card.Body>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  </Tab.Pane>
                  
                  {/* Previous Papers Tab */}
                  <Tab.Pane eventKey="papers">
                    <div className="papers-container">
                      <div className="table-responsive">
                        <table className="table table-hover papers-table">
                          <thead>
                            <tr className="table-light">
                              <th>Year</th>
                              <th>Difficulty Level</th>
                              <th>Question Paper</th>
                              <th>Answer Key</th>
                              <th>Analysis</th>
                            </tr>
                          </thead>
                          <tbody>
                            {papersData.map(paper => (
                              <tr key={paper.id}>
                                <td className="fw-bold">{paper.year}</td>
                                <td>
                                  <Badge bg={
                                    paper.difficulty === 'High' ? 'danger' : 
                                    paper.difficulty === 'Medium' ? 'warning' : 'success'
                                  }>
                                    {paper.difficulty}
                                  </Badge>
                                </td>
                                <td>
                                  <Button variant="outline-primary" size="sm" href={paper.paperLink}>
                                    <i className="bi bi-download me-1"></i> Download
                                  </Button>
                                </td>
                                <td>
                                  <Button variant="outline-success" size="sm" href={paper.answerKeyLink}>
                                    <i className="bi bi-file-earmark-check me-1"></i> Solutions
                                  </Button>
                                </td>
                                <td>
                                  <Button variant="link" size="sm">
                                    <i className="bi bi-bar-chart me-1"></i> View
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </Tab.Pane>
                  
                  {/* Reference Books Tab */}
                  <Tab.Pane eventKey="books">
                    <div className="books-container">
                      <Row className="g-4">
                        {booksData.map(book => (
                          <Col md={6} lg={4} key={book.id}>
                            <Card className="h-100 book-card shadow-sm">
                              <Row className="g-0">
                                <Col xs={4} className="book-cover-col">
                                  <div className="book-cover-wrapper">
                                    <Badge className="category-badge" bg="info">{book.category}</Badge>
                                    <img src={book.coverImg} alt={book.title} className="img-fluid rounded-start book-cover" />
                                  </div>
                                </Col>
                                <Col xs={8}>
                                  <Card.Body>
                                    <Card.Title className="h5 mb-1">{book.title}</Card.Title>
                                    <Card.Text className="text-muted small mb-2">{book.author}</Card.Text>
                                    <div className="d-flex align-items-center mb-2">
                                      <div className="rating-stars me-2">
                                        {'★'.repeat(Math.floor(book.rating))}
                                        {book.rating % 1 !== 0 ? '½' : ''}
                                        {'☆'.repeat(5 - Math.ceil(book.rating))}
                                      </div>
                                      <span className="rating-value">{book.rating}/5.0</span>
                                    </div>
                                    <Button variant="outline-primary" size="sm" className="mt-2">Read Reviews</Button>
                                  </Card.Body>
                                </Col>
                              </Row>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  </Tab.Pane>
                  
                  {/* Our Program Tab */}
                  <Tab.Pane eventKey="program">
                    <Row>
                      <Col lg={6} className="mb-4">
                        <div className="program-features">
                          <h3 className="h4 mb-4">Program Highlights</h3>
                          <div className="feature-grid">
                            <div className="feature-card">
                              <div className="feature-icon">👨‍🏫</div>
                              <h4>Expert Faculty</h4>
                              <p>Learn from PhD economists & IIM/IIT alumni</p>
                            </div>
                            <div className="feature-card">
                              <div className="feature-icon">💻</div>
                              <h4>Data Analysis Lab</h4>
                              <p>Hands-on training with econometric software</p>
                            </div>
                            <div className="feature-card">
                              <div className="feature-icon">📝</div>
                              <h4>Mock Tests</h4>
                              <p>Weekly assessments with personalized feedback</p>
                            </div>
                            <div className="feature-card">
                              <div className="feature-icon">📚</div>
                              <h4>Study Material</h4>
                              <p>Comprehensive notes and question banks</p>
                            </div>
                          </div>
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div className="program-timeline">
                          <h3 className="h4 mb-4">Course Timeline</h3>
                          <div className="timeline">
                            <div className="timeline-item">
                              <div className="timeline-dot"></div>
                              <div className="timeline-content">
                                <div className="timeline-date">Month 1-2</div>
                                <h4 className="timeline-title">Foundation Building</h4>
                                <p>Mastering core concepts in micro & macroeconomics</p>
                              </div>
                            </div>
                            <div className="timeline-item">
                              <div className="timeline-dot"></div>
                              <div className="timeline-content">
                                <div className="timeline-date">Month 3-4</div>
                                <h4 className="timeline-title">Mathematical Methods</h4>
                                <p>Focus on mathematical economics and statistics</p>
                              </div>
                            </div>
                            <div className="timeline-item">
                              <div className="timeline-dot"></div>
                              <div className="timeline-content">
                                <div className="timeline-date">Month 5-6</div>
                                <h4 className="timeline-title">Advanced Topics</h4>
                                <p>Covering econometrics and specialized fields</p>
                              </div>
                            </div>
                            <div className="timeline-item">
                              <div className="timeline-dot"></div>
                              <div className="timeline-content">
                                <div className="timeline-date">Month 7-8</div>
                                <h4 className="timeline-title">Test Series & Revision</h4>
                                <p>Intensive problem solving and comprehensive revision</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Tab.Pane>
                </Tab.Content>
              </div>
            </Tab.Container>
          )}
        </Container>
      </section>

      {/* Internship Section */}
      <section className="internship-section py-5">
        <Container>
          <div className="section-heading text-center mb-5">
            <Badge bg="success" className="mb-2">Career Enhancement</Badge>
            <h2 className="display-6 fw-bold">Economics + AI Internship Programs</h2>
            <p className="text-muted mx-auto" style={{ maxWidth: '700px' }}>Gain practical experience in applying economic theories through data-driven research and analysis</p>
          </div>

          <Row className="g-4">
            {internships.map(internship => (
              <Col md={6} lg={4} key={internship.id}>
                <Card className="h-100 internship-card">
                  <div className="internship-icon">{internship.icon}</div>
                  <Card.Body>
                    <Card.Title className="h4 mb-3">{internship.title}</Card.Title>
                    <Card.Text className="mb-4">{internship.description}</Card.Text>
                    
                    <div className="internship-details mb-4">
                      <div className="detail-item">
                        <span className="detail-label">Duration</span>
                        <span className="detail-value">{internship.duration}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Stipend</span>
                        <span className="detail-value">{internship.stipend}</span>
                      </div>
                    </div>
                    
                    <div className="requirements">
                      <h5 className="h6 requirements-title">Requirements</h5>
                      <ul className="requirements-list">
                        {internship.requirements.map((req, idx) => (
                          <li key={idx}>{req}</li>
                        ))}
                      </ul>
                    </div>
                  </Card.Body>
                  <Card.Footer className="bg-transparent border-0 pb-4">
                    <Button variant="primary" className="w-100">Apply for Internship</Button>
                  </Card.Footer>
                </Card>
              </Col>
            ))}
          </Row>

          <div className="internship-approach mt-5 bg-light p-4 rounded-lg shadow-sm">
            <Row className="align-items-center">
              <Col lg={5} className="mb-4 mb-lg-0">
                <div className="approach-content p-3">
                  <h3 className="h4 mb-4">Benefits of Our Integrated Approach</h3>
                  <div className="approach-features">
                    <div className="approach-feature d-flex mb-3">
                      <div className="approach-icon me-3">🔍</div>
                      <div>
                        <h4 className="h5 mb-1">Practical Application</h4>
                        <p className="mb-0">Apply theoretical economic concepts to real-world datasets and problems</p>
                      </div>
                    </div>
                    <div className="approach-feature d-flex mb-3">
                      <div className="approach-icon me-3">💼</div>
                      <div>
                        <h4 className="h5 mb-1">Industry Exposure</h4>
                        <p className="mb-0">Work on projects with our industry partners in finance, consulting, and policy</p>
                      </div>
                    </div>
                    <div className="approach-feature d-flex">
                      <div className="approach-icon me-3">📋</div>
                      <div>
                        <h4 className="h5 mb-1">Portfolio Development</h4>
                        <p className="mb-0">Build a professional portfolio showcasing your analytical and research skills</p>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
              <Col lg={7}>
                <div className="approach-stats">
                  <Row className="g-3">
                    <Col md={4}>
                      <div className="stat-card text-center">
                        <div className="stat-value">85%</div>
                        <div className="stat-label">Placement Rate</div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="stat-card text-center">
                        <div className="stat-value">25+</div>
                        <div className="stat-label">Partner Organizations</div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="stat-card text-center">
                        <div className="stat-value">12+</div>
                        <div className="stat-label">Research Papers</div>
                      </div>
                    </Col>
                  </Row>
                  <div className="approach-image mt-4">
                    <img src="/api/placeholder/600/300" alt="Economics data lab" className="img-fluid rounded" />
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default IITJAMEconomicsPage;

// Add CSS for styling
export const EconomicsPageCSS = `
/* General Styles */
.iit-jam-economics-page {
  font-family: 'Inter', sans-serif;
  color: #333;
}

.section-heading {
  margin-bottom: 3rem;
}

/* Hero Section */
.hero-section {
  background: linear-gradient(120deg, #2c3e50, #4b6cb7, #182848);
  padding: 6rem 0;
  position: relative;
  overflow: hidden;
}

.hero-content h1 {
  font-weight: 800;
  margin-bottom: 1.5rem;
}

.hero-image {
  position: relative;
}

.hero-image img {
  border-radius: 12px;
  transition: transform 0.3s ease;
}

.hero-image:hover img {
  transform: translateY(-5px);
}

.hero-shape-1,
.hero-shape-2 {
  position: absolute;
  border-radius: 50%;
  opacity: 0.2;
  z-index: 0;
}

.hero-shape-1 {
  width: 150px;
  height: 150px;
  background: linear-gradient(45deg, #4b6cb7, #182848);
  top: -30px;
  right: 10%;
}

.hero-shape-2 {
  width: 100px;
  height: 100px;
  background: linear-gradient(45deg, #2c3e50, #4b6cb7);
  bottom: -20px;
  left: 10%;
}

.stats-card {
  position: absolute;
  right: -20px;
  bottom: 30px;
  background-color: rgba(255, 255, 255, 0.95);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  padding: 15px;
  display: flex;
  width: 180px;
  z-index: 2;
}

.stats-item {
  flex: 1;
  text-align: center;
}

.stats-item h3 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #4b6cb7;
}

.stats-item p {
  margin: 0;
  font-size: 0.8rem;
  color: #666;
}

/* Coaching Section */
.coaching-section {
  background-color: #f8f9fa;
}

.nav-tabs-custom {
  display: inline-flex;
  background-color: #fff;
  border-radius: 999px;
  padding: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.nav-tabs-custom .nav-link {
  border-radius: 999px;
  padding: 10px 20px;
  color: #555;
  font-weight: 500;
  border: none;
  transition: all 0.3s;
}

.nav-tabs-custom .nav-link.active {
  background-color: #4b6cb7;
  color: white;
}

.tab-content-wrapper {
  background-color: #fff;
  border-radius: 12px;
}

/* Syllabus Tab */
.syllabus-card {
  transition: transform 0.3s;
}

.syllabus-card:hover {
  transform: translateY(-5px);
}

.syllabus-card .card-header {
  background: linear-gradient(45deg, #4b6cb7, #182848);
  color: white;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  padding: 15px 20px;
}

.topic-list {
  list-style-type: none;
  padding-left: 0;
}

.topic-icon {
  color: #4b6cb7;
  font-weight: bold;
  font-size: 18px;
}

/* Papers Tab */
.papers-table th, .papers-table td {
  vertical-align: middle;
}

/* Books Tab */
.book-card {
  overflow: hidden;
  transition: transform 0.3s;
}

.book-card:hover {
  transform: translateY(-5px);
}

.book-cover-wrapper {
  position: relative;
  height: 100%;
}

.book-cover {
  height: 100%;
  object-fit: cover;
}

.category-badge {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 2;
  font-size: 0.7rem;
}

.rating-stars {
  color: #ffc107;
  font-size: 14px;
}

.rating-value {
  font-size: 14px;
  font-weight: 600;
}

/* Program Tab */
.feature-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.feature-card {
  background-color: #f8f9fa;
  border-radius: 10px;
  padding: 20px;
  transition: all 0.3s ease;
}

.feature-card:hover {
  background-color: #e9ecef;
  transform: translateY(-3px);
}

.feature-icon {
  font-size: 2rem;
  margin-bottom: 15px;
}

.feature-card h4 {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.feature-card p {
  font-size: 0.9rem;
  margin-bottom: 0;
  color: #666;
}

.timeline {
  position: relative;
  padding-left: 30px;
}

.timeline:before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  width: 2px;
  height: 100%;
  background-color: #4b6cb7;
}

.timeline-item {
  position: relative;
  padding-bottom: 25px;
}

.timeline-dot {
  position: absolute;
  left: -39px;
  top: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #4b6cb7;
  border: 4px solid #fff;
  box-shadow: 0 0 0 2px #4b6cb7;
}

.timeline-date {
  display: inline-block;
  padding: 4px 10px;
  background-color: #4b6cb7;
  color: white;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 500;
  margin-bottom: 10px;
}

.timeline-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 5px;
}

/* Internship Section */
.internship-section {
  padding: 5rem 0;
}

.internship-card {
  border: none;
  border-radius: 12px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.07);
  position: relative;
  overflow: hidden;
  transition: transform 0.3s, box-shadow 0.3s;
}

.internship-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
}

.internship-icon {
  font-size: 2.5rem;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: -15px;
  right: 20px;
  background-color: #fff;
  border-radius: 30px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.internship-details {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.detail-item:last-child {
  margin-bottom: 0;
}

.detail-label {
  font-weight: 600;
  color: #555;
}

.detail-value {
  font-weight: 500;
  color: #4b6cb7;
}

.requirements-title {
  font-weight: 600;
  margin-bottom: 12px;
}

.requirements-list {
  padding-left: 20px;
  margin-bottom: 0;
}

.requirements-list li {
  margin-bottom: 8px;
  position: relative;
}

.requirements-list li:last-child {
  margin-bottom: 0;
}

/* Internship Approach Section */
.internship-approach {
  border-radius: 12px;
}

.approach-content {
  padding-right: 15px;
}

.approach-features {
  margin-top: 20px;
}

.approach-icon {
  font-size: 1.8rem;
  display: flex;
  align-items: center;
}

.approach-stats {
  padding: 15px;
}

.stat-card {
  background-color: white;
  border-radius: 10px;
  padding: 20px 15px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s;
}

.stat-card:hover {
  transform: translateY(-5px);
}

.stat-value {
  font-size: 1.8rem;
  font-weight: 700;
  color: #4b6cb7;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 0.9rem;
  color: #666;
}

.approach-image {
  margin-top: 20px;
  overflow: hidden;
  border-radius: 10px;
}

.approach-image img {
  transition: transform 0.5s;
}

.approach-image:hover img {
  transform: scale(1.05);
}

/* Responsive */
@media (max-width: 991px) {
  .feature-grid {
    grid-template-columns: 1fr;
  }
  
  .hero-section {
    padding: 4rem 0;
  }
  
  .stats-card {
    right: 50%;
    transform: translateX(50%);
    bottom: -30px;
  }
}

@media (max-width: 767px) {
  .nav-tabs-custom .nav-link {
    padding: 8px 12px;
    font-size: 0.9rem;
  }
  
  .stat-card {
    margin-bottom: 15px;
  }
}

@media (max-width: 575px) {
  .hero-content h1 {
    font-size: 2rem;
  }
  
  .nav-tabs-custom {
    flex-wrap: wrap;
  }
  
  .nav-tabs-custom .nav-item {
    width: 50%;
    margin-bottom: 5px;
  }
  
  .nav-tabs-custom .nav-link {
    width: 100%;
    text-align: center;
  }
}

/* Z-index */
.z-index-1 {
  z-index: 1;
}
  `;